import { Card, Stack, Typography } from '@mui/material'
import {
  CalendarToday,
  Category,
  CheckCircle,
  Cancel as CancelIcon,
  People,
  Person,
  ReceiptLong,
  TheaterComedy,
  Warning as WarningIcon
} from '@mui/icons-material'
import { Email, Phone } from '@mui/icons-material'
import React from 'react'
import PropTypes from 'prop-types'

const statusIcons = {
  completed: { icon: CheckCircle, color: 'success.main' },
  pending: { icon: WarningIcon, color: 'warning.main' },
  failed: { icon: CancelIcon, color: 'error.main' }
}

const paymentDetailsShape = PropTypes.shape({
  status: PropTypes.string,
  metadata: PropTypes.shape({
    show: PropTypes.string,
    schedule: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    selectedSeats: PropTypes.string,
    categories: PropTypes.string
  })
})

const adminDataShape = PropTypes.shape({
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  email: PropTypes.string,
  phone: PropTypes.string,
  seats: PropTypes.array,
  rows: PropTypes.array,
  categories: PropTypes.array,
  paid: PropTypes.string,
  admin: PropTypes.bool
})

export default function PaymentCard({ booking, paymentDetails, adminData }) {
  const status = (paymentDetails && paymentDetails.status) || (adminData && adminData.paid)
  const meta = (paymentDetails && paymentDetails.metadata) || null

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2, p: 2 }}>
      <Typography
        variant='subtitle1'
        fontWeight='bold'
        color='primary'
        sx={{ mb: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        Paiement
      </Typography>
      <InfoRow icon={ReceiptLong} label='ID de paiement' value={booking} mono />
      {status && <StatusRow status={status} />}
      {meta ? (
        <>
          <InfoRow icon={TheaterComedy} label='Spectacle' value={meta.show || 'N/A'} />
          <InfoRow icon={CalendarToday} label='Horaire' value={meta.schedule || 'N/A'} />
          <InfoRow icon={Person} label='Client' value={`${meta.firstName || ''} ${meta.lastName || ''}`.trim() || '—'} />
          {meta.email && <InfoRow icon={Email} label='Email' value={meta.email} />}
          {meta.phone && <InfoRow icon={Phone} label='Téléphone' value={meta.phone} />}
          <InfoRow icon={People} label='Sièges réservés' value={meta.selectedSeats || 'N/A'} />
          <InfoRow icon={Category} label='Catégories' value={meta.categories || 'N/A'} />
        </>
      ) : (
        <>
          {adminData && adminData.firstName && adminData.lastName && (
            <InfoRow icon={Person} label='Client' value={`${adminData.firstName} ${adminData.lastName}`} />
          )}
          {adminData?.email && <InfoRow icon={Email} label='Email' value={adminData.email} />}
          {adminData?.phone && <InfoRow icon={Phone} label='Téléphone' value={adminData.phone} />}
          {(adminData?.seats || adminData?.rows) && (
            <InfoRow icon={People} label='Sièges réservés' value={Array.isArray(adminData.seats) ? adminData.seats.join(', ') : '—'} />
          )}
          {adminData?.categories && (
            <InfoRow
              icon={Category}
              label='Catégories'
              value={Array.isArray(adminData.categories) ? JSON.stringify(adminData.categories) : 'N/A'}
            />
          )}
        </>
      )}
    </Card>
  )
}

function StatusRow({ status }) {
  const config = statusIcons[status] || { icon: CheckCircle, color: 'text.secondary' }
  return (
    <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mt: 1 }}>
      <config.icon sx={{ color: config.color, fontSize: 22 }} />
      <Typography variant='body2' color='text.secondary' sx={{ width: 100, flexShrink: 0 }}>
        Statut:
      </Typography>
      <Typography variant='body1' fontWeight={600}>
        {status}
      </Typography>
    </Stack>
  )
}

function InfoRow({ icon: Icon, label, value, mono }) {
  return (
    <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mt: 1 }}>
      <Icon sx={{ color: 'text.secondary', fontSize: 22 }} />
      <Typography variant='body2' color='text.secondary' sx={{ width: 100, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant='body1' fontWeight={600} sx={mono ? { fontFamily: 'monospace' } : undefined}>
        {value}
      </Typography>
    </Stack>
  )
}

PaymentCard.propTypes = {
  booking: PropTypes.string.isRequired,
  paymentDetails: paymentDetailsShape,
  adminData: adminDataShape
}

StatusRow.propTypes = {
  status: PropTypes.string.isRequired
}

InfoRow.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  mono: PropTypes.bool
}
